import {
  BarChart3,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  DollarSign,
  ExternalLink,
  Flame,
  GraduationCap,
  GripVertical,
  History,
  MessageSquare,
  Pencil,
  Plus,
  Trash2,
  TrendingUp,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { DailySurvey } from '@/components/DailySurvey'
import { PoweredByFooter } from '@/components/PoweredByFooter'
import { ServiceBanner } from '@/components/ServiceBanner'
import { Card } from '@/components/ui/card'
import { Carousel, type CarouselApi, CarouselContent, CarouselItem } from '@/components/ui/carousel'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useBanner } from '@/hooks/useBanner'
import { type SurveyHistoryItem, useSurvey } from '@/hooks/useSurvey'
import { trackEvent } from '@/lib/analytics'

type StatsMode = 'nz' | 'custom'

function formatPrice(price: number): string {
  if (price >= 1_000_000) return `$${(price / 1_000_000).toFixed(2)}M`
  return `$${(price / 1_000).toFixed(0)}K`
}

interface RegionStat {
  displayName: string
  medianPrice: number | null
  daysToSell: number | null
  noOfSales: number | null
  percChange: number | null
}

interface SuburbStat {
  suburbID: number
  suburbName: string
  taName: string
  medianPrice: number | null
  daysToSell: number | null
  noOfSales: number | null
  percChange: number | null
}

interface DisplayStat {
  label: string
  sublabel: string
  medianPrice: number | null
  daysToSell: number | null
  noOfSales: number | null
  percChange: number | null
}

async function fetchAllRegionStats(): Promise<RegionStat[]> {
  const regionsRes = await fetch('api/report/region?region=all')
  const allRegions: { regionId: number; region: string }[] = await regionsRes.json()
  const nzRegions = allRegions
    .filter((r) => r.regionId < 50)
    .map((r) => ({ displayName: r.region.replace(/ Region$/i, '').trim(), regionId: r.regionId }))
    .filter((v, i, a) => a.findIndex((x) => x.displayName === v.displayName) === i)
  const now = new Date()
  const dateTo = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
  const past = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())
  const dateFrom = `${past.getFullYear()}-${String(past.getMonth() + 1).padStart(2, '0')}-${String(past.getDate()).padStart(2, '0')}`
  const results = await Promise.all(
    nzRegions.map(async ({ displayName, regionId }) => {
      try {
        const params = new URLSearchParams({
          GroupingType: '2',
          Grouping1: String(regionId),
          Grouping2: 'all',
          Frequency: '1',
          DateFrom: dateFrom,
          DateTo: dateTo,
        })
        const res = await fetch(`api/report/market-insights?${params}`)
        const records: {
          Date: string
          MedianSalePrice: number
          DaysToSell: number
          NoOfSales: number
          PercSalePriceDiffLastPeriod: number
        }[] = await res.json()
        const latest = [...records].sort((a, b) => a.Date.localeCompare(b.Date)).pop()
        return {
          displayName,
          medianPrice: latest?.MedianSalePrice ?? null,
          daysToSell: latest?.DaysToSell ?? null,
          noOfSales: latest?.NoOfSales ?? null,
          percChange: latest?.PercSalePriceDiffLastPeriod ?? null,
        }
      } catch {
        return {
          displayName,
          medianPrice: null,
          daysToSell: null,
          noOfSales: null,
          percChange: null,
        }
      }
    }),
  )
  return results
}

async function fetchSuburbStats(
  suburbs: { suburbID: number; suburbName: string; taName: string }[],
): Promise<SuburbStat[]> {
  const now = new Date()
  const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
  const dateTo = fmt(new Date(now.getFullYear(), now.getMonth() + 1, 1))
  const dateFrom = fmt(new Date(now.getFullYear(), now.getMonth() - 3, 1))
  return Promise.all(
    suburbs.map(async (suburb) => {
      try {
        const params = new URLSearchParams({
          GroupingType: '5',
          Grouping1: String(suburb.suburbID),
          Grouping2: 'all',
          Frequency: '1',
          DateFrom: dateFrom,
          DateTo: dateTo,
        })
        const res = await fetch(`api/report/market-insights?${params}`)
        const records: {
          Date: string
          MedianSalePrice: number
          DaysToSell: number
          NoOfSales: number
          PercSalePriceDiffLastPeriod: number
        }[] = await res.json()
        const latest = [...records]
          .filter((r) => r.MedianSalePrice > 0)
          .sort((a, b) => b.Date.localeCompare(a.Date))[0]
        return {
          suburbID: suburb.suburbID,
          suburbName: suburb.suburbName,
          taName: suburb.taName,
          medianPrice: latest?.MedianSalePrice ?? null,
          daysToSell: latest?.DaysToSell ?? null,
          noOfSales: latest?.NoOfSales ?? null,
          percChange: latest?.PercSalePriceDiffLastPeriod ?? null,
        }
      } catch {
        return {
          suburbID: suburb.suburbID,
          suburbName: suburb.suburbName,
          taName: suburb.taName,
          medianPrice: null,
          daysToSell: null,
          noOfSales: null,
          percChange: null,
        }
      }
    }),
  )
}

const NEWS_CACHE_TTL = 60 * 60 * 1000

function getNewsCached(): NewsItem[] | null {
  try {
    const raw = localStorage.getItem('reinz_news_items')
    if (!raw) return null
    const { data, fetchedAt } = JSON.parse(raw)
    if (Date.now() - fetchedAt >= NEWS_CACHE_TTL) return null
    return data as NewsItem[]
  } catch {
    return null
  }
}

function setNewsCached(data: NewsItem[]): void {
  try {
    localStorage.setItem('reinz_news_items', JSON.stringify({ data, fetchedAt: Date.now() }))
  } catch {}
}

function savePreference(userId: string, key: string, value: string): void {
  if (!userId) return
  fetch('api/user-preferences', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, preferenceKey: key, preferenceValue: value }),
  }).catch(() => {})
}

interface Shortcut {
  id: string
  name: string
  url: string
}

const REQUIRED_LINKS = [
  { name: 'REA.govt.nz', url: 'https://www.rea.govt.nz' },
  { name: 'realestate.co.nz', url: 'https://www.realestate.co.nz' },
]

function getFavicon(url: string): string {
  try {
    const domain = new URL(url).hostname
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`
  } catch {
    return ''
  }
}

interface HomeScreenProps {
  onNavigate: (tab: string) => void
  username?: string
  partyId?: string
}

function getFirstName(username: string): string {
  const parts = username.split(/[\s@]/)
  const titles = ['mrs', 'ms', 'mr', 'miss']
  const hasTitle = titles.includes(parts[0].toLowerCase())
  const name = hasTitle ? parts[1] : parts[0]
  return name
}

const quickActions = [
  { id: 'ask', label: 'AskREINZ', icon: MessageSquare },
  { id: 'valuation', label: 'Estimate', icon: DollarSign },
  { id: 'insights', label: 'Insights', icon: TrendingUp },
  { id: 'events', label: 'Events', icon: CalendarDays },
  { id: 'education', label: 'Education', icon: GraduationCap },
]

interface NewsItem {
  title: string
  description: string
  url: string
  imageUrl?: string
  publishDate?: string
}

const PROMO_BANNER_EXPIRY = new Date('2026-08-01T12:00:00Z') // Aug 1 midnight NZ (UTC+12)
const PROMO_SURVEYS_NEEDED = 5
const PROMO_LAST_SHOWN_KEY = 'reinz_promo_last_shown'
function getNzDateString(): string {
  // en-CA always returns YYYY-MM-DD — unambiguous regardless of device locale
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Pacific/Auckland' })
}
function shouldShowPromoToday(): boolean {
  if (new Date() >= PROMO_BANNER_EXPIRY) return false
  try {
    const lastShown = localStorage.getItem(PROMO_LAST_SHOWN_KEY)
    if (lastShown === getNzDateString()) return false
  } catch {
    /* ignore */
  }
  return true
}

const AWARDS_BANNER_EXPIRY = new Date('2026-08-20T12:00:00Z') // Aug 20 midnight NZ
const AWARDS_URL =
  'https://www.reinz.co.nz/Web/Web/News/News-Articles/News-Industry/Awards_finalist.aspx?name=Awards_finalist'
function shouldShowAwardsBanner(): boolean {
  return new Date() < AWARDS_BANNER_EXPIRY
}

const AWARDS_TICKET_BANNER_EXPIRY = new Date('2026-09-02T17:00:00+12:00') // 2 Sept 5pm NZ
const AWARDS_TICKET_URL = 'https://awardsforexcellence.reinz.co.nz/registrationform'
function shouldShowAwardsTicketBanner(): boolean {
  return new Date() < AWARDS_TICKET_BANNER_EXPIRY
}

export function HomeScreen({ onNavigate, username = 'Agent', partyId = '' }: HomeScreenProps) {
  const [showSurvey, setShowSurvey] = useState(false)
  const [surveyExpanded, setSurveyExpanded] = useState(false)
  const [showPromoBanner, setShowPromoBanner] = useState(false)
  const [showAwardsBanner, setShowAwardsBanner] = useState(shouldShowAwardsBanner)
  const [showAwardsTicketBanner, setShowAwardsTicketBanner] = useState(shouldShowAwardsTicketBanner)
  const displayName = getFirstName(username)

  const [statsRefreshKey, setStatsRefreshKey] = useState(0)

  const [statsMode, setStatsModeState] = useState<StatsMode>('nz')
  const [statsModeReady, setStatsModeReady] = useState(false)
  const [pinnedSuburbRaw, setPinnedSuburbRaw] = useState<string | null>(null)
  const [customSelectionRaw, setCustomSelectionRaw] = useState<string | null>(null)
  const [hasCustomMultiSelection, setHasCustomMultiSelection] = useState(false)

  const setStatsMode = (mode: StatsMode) => {
    setStatsModeState(mode)
    savePreference(partyId, 'reinz_home_stats_mode', mode)
    trackEvent('home_stats_mode_changed', { mode })
  }

  // Single bulk preferences fetch — replaces all individual preference fetches on mount
  useEffect(() => {
    if (!partyId) {
      setStatsModeReady(true)
      return
    }
    fetch(`api/user-preferences?userId=${encodeURIComponent(partyId)}`)
      .then((r) => (r.ok ? r.json() : []))
      .catch(() => [])
      .then((prefs: { preferenceKey: string; preferenceValue: string }[]) => {
        const get = (key: string) =>
          prefs.find((p) => p.preferenceKey === key)?.preferenceValue ?? null

        const mode = get('reinz_home_stats_mode')
        if (mode === 'custom' || mode === 'local') setStatsModeState('custom')
        else if (mode === 'nz') setStatsModeState('nz')

        const shortcutsRaw = get('my_shortcuts')
        if (shortcutsRaw) {
          try {
            setShortcuts(JSON.parse(shortcutsRaw))
          } catch {
            /* ignore */
          }
        }

        setPinnedSuburbRaw(get('reinz_pinned_suburb'))
        setCustomSelectionRaw(get('custom_stats_selection'))

        setStatsModeReady(true)
      })
  }, [partyId])

  const [displayStats, setDisplayStats] = useState<DisplayStat[]>([])
  const [statsIndex, setStatsIndex] = useState(0)
  const [statsNoDataMsg, setStatsNoDataMsg] = useState<string | null>(null)
  const [visible, setVisible] = useState(true)
  const [carouselApi, setCarouselApi] = useState<CarouselApi>()
  const [carouselIndex, setCarouselIndex] = useState(0)
  const [newsItems, setNewsItems] = useState<NewsItem[]>([])
  const [showHistory, setShowHistory] = useState(false)
  const [historyItems, setHistoryItems] = useState<SurveyHistoryItem[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [expandedHistory, setExpandedHistory] = useState<Set<number>>(new Set())
  const [activeHomeTab, setActiveHomeTab] = useState<'quick' | 'shortcuts'>('quick')
  const [shortcuts, setShortcuts] = useState<Shortcut[]>([])
  const [shortcutModal, setShortcutModal] = useState<{ open: boolean; editing: Shortcut | null }>({
    open: false,
    editing: null,
  })
  const [shortcutForm, setShortcutForm] = useState({ name: '', url: '' })
  const [dragIndex, setDragIndex] = useState<number | null>(null)

  useEffect(() => {
    if (!partyId || !shouldShowPromoToday()) return
    fetch(`api/survey/response-count?userId=${encodeURIComponent(partyId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const count = data?.count ?? 0
        if (count >= PROMO_SURVEYS_NEEDED) return // already qualified
        // NZ midnight: Jul 31 midnight NZ = Jul 31T12:00:00Z (UTC+12)
        const competitionEnd = new Date('2026-07-31T12:00:00Z')
        // Use en-CA for YYYY-MM-DD format, then append NZ offset so midnight is correct
        const nzDateStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Pacific/Auckland' })
        const nowNz = new Date(`${nzDateStr}T00:00:00+12:00`)
        const daysRemaining =
          Math.floor((competitionEnd.getTime() - nowNz.getTime()) / 86400000) + 1
        if (daysRemaining < PROMO_SURVEYS_NEEDED - count) return // can't qualify in time
        setShowPromoBanner(true)
      })
      .catch(() => {
        setShowPromoBanner(true)
      }) // show on error — better than silently hiding
  }, [partyId])

  const dismissPromoBanner = () => {
    setShowPromoBanner(false)
    try {
      localStorage.setItem(PROMO_LAST_SHOWN_KEY, getNzDateString())
    } catch {
      /* ignore */
    }
  }

  const dismissAwardsBanner = () => {
    setShowAwardsBanner(false)
  }

  const dismissAwardsTicketBanner = () => {
    setShowAwardsTicketBanner(false)
  }

  useEffect(() => {
    const cached = getNewsCached()
    if (cached) {
      setNewsItems(cached)
      return
    }
    fetch('api/news/listing')
      .then((res) => res.json())
      .then((data: NewsItem[]) => {
        if (Array.isArray(data)) {
          setNewsItems(data)
          setNewsCached(data)
        }
      })
      .catch(() => {})
  }, [])

  // Load stats based on selected mode — wait for DB to confirm mode first
  useEffect(() => {
    if (!statsModeReady) return
    setDisplayStats([])
    setStatsIndex(0)
    setStatsNoDataMsg(null)

    if (statsMode === 'nz') {
      fetchAllRegionStats()
        .then((stats) => {
          const display = stats
            .filter((s) => s.medianPrice != null)
            .map((s) => ({
              label: s.displayName,
              sublabel: '',
              medianPrice: s.medianPrice,
              daysToSell: s.daysToSell,
              noOfSales: s.noOfSales,
              percChange: s.percChange,
            }))
          setDisplayStats(display)
        })
        .catch(() => {
          setStatsNoDataMsg('Unable to load market stats')
        })
      return
    }

    if (!partyId) return

    // custom mode — default suburb first, then multi-suburb selection if set
    const loadCustomStats = async () => {
      type Suburb = { suburbID: number; suburbName: string; taName: string }

      if (!pinnedSuburbRaw) {
        setStatsNoDataMsg('Set your default suburb in My Profile to see local stats here')
        setHasCustomMultiSelection(false)
        return
      }

      let defaultSuburb: Suburb | null = null
      try {
        defaultSuburb = JSON.parse(pinnedSuburbRaw)
      } catch {
        /* ignore */
      }
      if (!defaultSuburb?.suburbID) {
        setStatsNoDataMsg('Set your default suburb in My Profile to see local stats here')
        setHasCustomMultiSelection(false)
        return
      }

      // Get multi-suburb selection: sessionStorage first, then pre-loaded value
      let customSuburbs: Suburb[] = []
      const sessionRaw = sessionStorage.getItem('custom_stats_suburbs')
      if (sessionRaw) {
        try {
          customSuburbs = JSON.parse(sessionRaw)
        } catch {
          /* ignore */
        }
      }
      if (customSuburbs.length === 0 && customSelectionRaw) {
        try {
          const parsed = JSON.parse(customSelectionRaw) as { suburbs: Suburb[] }
          if (parsed.suburbs?.length) customSuburbs = parsed.suburbs
        } catch {
          /* ignore */
        }
      }

      setHasCustomMultiSelection(customSuburbs.length > 0)

      // Build list: default suburb first, then remaining custom suburbs
      const others = customSuburbs.filter((s) => s.suburbID !== defaultSuburb!.suburbID)
      const suburbs: Suburb[] = [defaultSuburb, ...others]

      try {
        const stats = await fetchSuburbStats(suburbs)
        const valid = stats.filter((s) => s.medianPrice != null)
        if (valid.length === 0) {
          setStatsNoDataMsg('No recent data available for your suburb')
          return
        }
        setDisplayStats(
          valid.map((s) => ({
            label: s.suburbName,
            sublabel: s.taName,
            medianPrice: s.medianPrice,
            daysToSell: s.daysToSell,
            noOfSales: s.noOfSales,
            percChange: s.percChange,
          })),
        )
      } catch {
        setStatsNoDataMsg('Unable to load suburb stats')
      }
    }
    loadCustomStats()
  }, [partyId, statsMode, statsModeReady, pinnedSuburbRaw, customSelectionRaw, statsRefreshKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-cycle when multiple stats
  useEffect(() => {
    if (displayStats.length <= 1) return
    const interval = setInterval(() => {
      setVisible(false)
      setTimeout(() => {
        setStatsIndex((i) => (i + 1) % displayStats.length)
        setVisible(true)
      }, 300)
    }, 5000)
    return () => clearInterval(interval)
  }, [displayStats.length])

  const banner = useBanner('home')
  const { data: surveyData, loading: surveyLoading, submitResponse } = useSurvey(partyId)
  const currentStreak = surveyData?.currentStreak ?? 0
  const hasRespondedToday = surveyData?.hasRespondedToday ?? false

  useEffect(() => {
    trackEvent('screen_viewed', { screen: 'home' })
  }, [])

  useEffect(() => {
    const handler = () => {
      if (statsMode === 'custom') {
        setCustomSelectionRaw(null)
        setStatsRefreshKey((k) => k + 1)
      }
    }
    window.addEventListener('custom-stats-cleared', handler)
    return () => window.removeEventListener('custom-stats-cleared', handler)
  }, [statsMode])

  useEffect(() => {
    const handler = () => {
      if (statsMode === 'custom') setStatsRefreshKey((k) => k + 1)
    }
    window.addEventListener('custom-stats-updated', handler)
    return () => window.removeEventListener('custom-stats-updated', handler)
  }, [statsMode])

  function saveShortcuts(list: Shortcut[]) {
    setShortcuts(list)
    savePreference(partyId, 'my_shortcuts', JSON.stringify(list))
  }

  function openHistory() {
    setShowHistory(true)
    if (!partyId) return
    setHistoryLoading(true)
    fetch(`api/survey/history?userId=${encodeURIComponent(partyId)}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        setHistoryItems(Array.isArray(d) ? d : [])
      })
      .catch(() => {})
      .finally(() => setHistoryLoading(false))
  }

  function handleShortcutSave() {
    const name = shortcutForm.name.trim()
    let url = shortcutForm.url.trim()
    if (!name || !url) return
    if (!url.startsWith('http')) url = `https://${url}`
    if (shortcutModal.editing) {
      saveShortcuts(
        shortcuts.map((s) => (s.id === shortcutModal.editing!.id ? { ...s, name, url } : s)),
      )
    } else {
      saveShortcuts([...shortcuts, { id: Date.now().toString(), name, url }])
    }
    setShortcutModal({ open: false, editing: null })
  }

  function handleShortcutDelete(id: string) {
    if (!window.confirm('Remove this shortcut?')) return
    saveShortcuts(shortcuts.filter((s) => s.id !== id))
  }

  useEffect(() => {
    if (!carouselApi) return
    const onSelect = () => setCarouselIndex(carouselApi.selectedScrollSnap())
    carouselApi.on('select', onSelect)
    const interval = setInterval(() => carouselApi.scrollNext(), 5000)
    return () => {
      carouselApi.off('select', onSelect)
      clearInterval(interval)
    }
  }, [carouselApi])

  const modeToggle = (
    <div className="flex bg-primary-foreground/15 rounded-full p-0.5">
      {(['nz', 'custom'] as StatsMode[]).map((mode) => (
        <button
          key={mode}
          onClick={() => setStatsMode(mode)}
          className={`px-2 py-0.5 rounded-full text-3xs font-medium transition-all ${
            statsMode === mode
              ? 'bg-primary-foreground text-primary shadow-sm'
              : 'text-primary-foreground/60 hover:text-primary-foreground'
          }`}
        >
          {mode === 'nz' ? 'All' : 'Custom'}
        </button>
      ))}
    </div>
  )

  return (
    <div className="flex flex-col min-h-full pb-4">
      {/* Gift promo modal — shown once per day until 5 surveys completed or 31 July */}
      {showPromoBanner && (
        <div
          className="fixed inset-0 z-100 flex items-center justify-center bg-black/60 px-4"
          onClick={dismissPromoBanner}
        >
          <div className="relative w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={dismissPromoBanner}
              aria-label="Dismiss"
              className="absolute -top-3 -right-3 z-10 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center text-gray-600 hover:text-gray-900 text-lg font-bold"
            >
              ×
            </button>
            <img
              src="/gift-banner.jpg"
              alt="Win a Samsung Galaxy A17 — complete the Daily Survey at least five times by 31 July"
              className="w-full rounded-2xl shadow-2xl"
            />
            <button
              onClick={() => {
                dismissPromoBanner()
                setShowSurvey(true)
              }}
              className="mt-3 w-full bg-primary text-primary-foreground font-semibold py-3 rounded-xl shadow-lg hover:bg-primary/90 active:scale-[0.98] transition-all text-sm"
            >
              Complete Today's Survey →
            </button>
          </div>
        </div>
      )}
      {/* Awards 2026 finalist banner — shown on every login until Aug 20 expiry */}
      {showAwardsBanner && (
        <div
          className="fixed inset-0 z-100 flex items-center justify-center bg-black/60 px-4"
          onClick={dismissAwardsBanner}
        >
          <div className="relative w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={dismissAwardsBanner}
              aria-label="Dismiss"
              className="absolute -top-3 -right-3 z-10 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center text-gray-600 hover:text-gray-900 text-lg font-bold"
            >
              ×
            </button>
            <img
              src="/awards26-finalist.jpg"
              alt="2026 REINZ Awards Finalists are here!"
              className="w-full rounded-2xl shadow-2xl"
            />
            <button
              onClick={() => {
                dismissAwardsBanner()
                window.open(AWARDS_URL, '_blank', 'noopener,noreferrer')
              }}
              className="mt-3 w-full bg-primary text-primary-foreground font-semibold py-3 rounded-xl shadow-lg hover:bg-primary/90 active:scale-[0.98] transition-all text-sm"
            >
              See the Finalists →
            </button>
          </div>
        </div>
      )}
      {/* Awards 2026 ticket final-call banner — shown on every login until 2 Sept 5pm NZ expiry */}
      {showAwardsTicketBanner && (
        <div
          className="fixed inset-0 z-100 flex items-center justify-center bg-black/60 px-4"
          onClick={dismissAwardsTicketBanner}
        >
          <div className="relative w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={dismissAwardsTicketBanner}
              aria-label="Dismiss"
              className="absolute -top-3 -right-3 z-10 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center text-gray-600 hover:text-gray-900 text-lg font-bold"
            >
              ×
            </button>
            <img
              src="/awards26-popup-message.jpg"
              alt="REINZ Awards Gala Dinner — final call, ticket sales close 2 Sept 5pm"
              className="w-full rounded-2xl shadow-2xl"
            />
            <button
              onClick={() => {
                dismissAwardsTicketBanner()
                window.open(AWARDS_TICKET_URL, '_blank', 'noopener,noreferrer')
              }}
              className="mt-3 w-full bg-primary text-primary-foreground font-semibold py-3 rounded-xl shadow-lg hover:bg-primary/90 active:scale-[0.98] transition-all text-sm"
            >
              Buy Tickets →
            </button>
          </div>
        </div>
      )}
      {/* Welcome Banner */}
      <div className="bg-gradient-reinz px-4 py-6 -mx-4 -mt-4 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-semibold text-primary-foreground mb-1 font-heading">
              Kia ora {displayName}
            </h1>
            <p className="text-primary-foreground/80 text-sm">Here's what's happening today</p>
          </div>
          {modeToggle}
        </div>

        {displayStats.length > 0 ? (
          <>
            <div
              className="flex gap-4 mt-4 transition-opacity duration-300"
              style={{ opacity: visible ? 1 : 0 }}
            >
              {(() => {
                const stat = displayStats[statsIndex]
                return (
                  <>
                    <div className="flex-1 bg-primary-foreground/10 rounded-xl p-3 backdrop-blur-xs">
                      <p className="text-primary-foreground/70 text-xs truncate">{stat.label}</p>
                      <p className="text-primary-foreground font-semibold text-lg">
                        {stat.medianPrice != null ? formatPrice(stat.medianPrice) : '--'}
                      </p>
                      <p
                        className={`text-xs font-medium ${
                          stat.percChange == null || stat.percChange === 0
                            ? 'text-primary-foreground/60'
                            : stat.percChange > 0
                              ? 'text-green-400'
                              : 'text-red-300'
                        }`}
                      >
                        {stat.percChange != null && stat.percChange !== 0
                          ? `${stat.percChange > 0 ? '+' : ''}${stat.percChange.toFixed(1)}% vs prior`
                          : 'Median Sale Price'}
                      </p>
                    </div>
                    <div className="flex-1 bg-primary-foreground/10 rounded-xl p-3 backdrop-blur-xs">
                      <p className="text-primary-foreground/70 text-xs">Days to Sell</p>
                      <p className="text-primary-foreground font-semibold text-lg">
                        {stat.daysToSell != null && stat.daysToSell > 0 ? stat.daysToSell : '--'}
                      </p>
                      <p className="text-primary-foreground/60 text-xs">{stat.sublabel}</p>
                    </div>
                    <div className="flex-1 bg-primary-foreground/10 rounded-xl p-3 backdrop-blur-xs">
                      <p className="text-primary-foreground/70 text-xs">Monthly Sales</p>
                      <p className="text-primary-foreground font-semibold text-lg">
                        {stat.noOfSales != null ? stat.noOfSales.toLocaleString() : '--'}
                      </p>
                      <p className="text-primary-foreground/60 text-xs">{stat.sublabel}</p>
                    </div>
                  </>
                )
              })()}
            </div>
            {statsMode === 'custom' && !hasCustomMultiSelection && (
              <p className="text-primary-foreground/60 text-3xs mt-2">
                Tip: add suburbs in{' '}
                <button
                  onClick={() => {
                    sessionStorage.setItem('insights_initial_tab', 'custom')
                    onNavigate('insights')
                  }}
                  className="underline hover:text-primary-foreground/80"
                >
                  Market Insights → Multi-Suburb
                </button>{' '}
                to cycle through multiple suburbs here.
              </p>
            )}
          </>
        ) : (
          <>
            <div className="mt-4 w-full bg-primary-foreground/10 rounded-xl p-3 backdrop-blur-xs">
              {statsNoDataMsg === 'multi-suburb-empty' ? (
                <>
                  <p className="text-primary-foreground/80 text-xs">No suburbs selected</p>
                  <p className="text-primary-foreground text-sm font-medium mt-0.5">
                    Go to Insights → Multi-Suburb to add suburbs and compare them here.{' '}
                    <button
                      onClick={() => {
                        sessionStorage.setItem('insights_initial_tab', 'custom')
                        onNavigate('insights')
                      }}
                      className="text-primary-foreground/80 hover:text-primary-foreground underline"
                    >
                      Try it →
                    </button>
                  </p>
                </>
              ) : statsNoDataMsg ? (
                <>
                  <p className="text-primary-foreground/80 text-xs">No data available</p>
                  <p className="text-primary-foreground text-sm font-medium mt-0.5">
                    {statsNoDataMsg}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-primary-foreground/80 text-xs">Loading stats…</p>
                  <div className="h-5 mt-1 w-24 bg-primary-foreground/10 rounded animate-pulse" />
                </>
              )}
            </div>
          </>
        )}
      </div>

      <ServiceBanner banner={banner} />

      {/* Quick Actions / My Shortcuts tabs */}
      <section className="mb-6">
        <div className="flex border-b border-border mb-4">
          <button
            onClick={() => setActiveHomeTab('quick')}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${activeHomeTab === 'quick' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'}`}
          >
            Quick Actions
          </button>
          <button
            onClick={() => setActiveHomeTab('shortcuts')}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${activeHomeTab === 'shortcuts' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'}`}
          >
            My Shortcuts
          </button>
        </div>

        {activeHomeTab === 'quick' && (
          <div className="grid grid-cols-5 gap-2">
            {quickActions.map((action) => {
              const Icon = action.icon
              return (
                <button
                  key={action.id}
                  onClick={() => {
                    trackEvent('quick_action_clicked', { action: action.label })
                    onNavigate(
                      action.id === 'cma' || action.id === 'valuation' ? 'tools' : action.id,
                    )
                  }}
                  className="quick-action"
                >
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-primary">
                      <Icon className="w-5 h-5 text-primary-foreground" />
                    </div>
                    {action.id === 'ask' && (
                      <span className="absolute -top-3 -left-1 text-[8px] font-semibold text-white bg-primary leading-none whitespace-nowrap px-1 py-0.5 rounded-sm">
                        Beta
                      </span>
                    )}
                  </div>
                  <span className="text-3xs font-medium text-foreground text-center leading-tight">
                    {action.label}
                  </span>
                </button>
              )
            })}
          </div>
        )}

        {activeHomeTab === 'shortcuts' && (
          <div>
            <p className="text-xs text-muted-foreground mb-4">
              Connect external links you use most often
            </p>

            <p className="text-xs font-semibold text-foreground mb-2">Your shortcuts</p>
            <div className="space-y-2">
              {REQUIRED_LINKS.map((link) => (
                <a
                  key={link.url}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <img
                      src={getFavicon(link.url)}
                      alt=""
                      className="w-5 h-5"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                      }}
                    />
                  </div>
                  <span className="flex-1 text-sm font-medium flex items-center gap-1">
                    {link.name} <ExternalLink className="w-3 h-3 text-muted-foreground" />
                  </span>
                  <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium shrink-0">
                    🔒 Required
                  </span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </a>
              ))}
              {shortcuts.map((shortcut, index) => (
                <div
                  key={shortcut.id}
                  draggable
                  onDragStart={() => setDragIndex(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDragEnd={() => setDragIndex(null)}
                  onDrop={() => {
                    if (dragIndex === null || dragIndex === index) {
                      setDragIndex(null)
                      return
                    }
                    const reordered = [...shortcuts]
                    const [moved] = reordered.splice(dragIndex, 1)
                    reordered.splice(index, 0, moved)
                    saveShortcuts(reordered)
                    setDragIndex(null)
                  }}
                  className={`flex items-center gap-3 p-3 rounded-xl border border-border bg-card transition-opacity ${dragIndex === index ? 'opacity-40' : ''}`}
                >
                  <a
                    href={shortcut.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"
                  >
                    <img
                      src={getFavicon(shortcut.url)}
                      alt=""
                      className="w-5 h-5"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                      }}
                    />
                  </a>
                  <a
                    href={shortcut.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 min-w-0 flex items-center gap-1"
                  >
                    <span className="text-sm font-medium truncate">{shortcut.name}</span>
                    <ExternalLink className="w-3 h-3 text-muted-foreground shrink-0" />
                  </a>
                  <button
                    onClick={() => {
                      setShortcutForm({ name: shortcut.name, url: shortcut.url })
                      setShortcutModal({ open: true, editing: shortcut })
                    }}
                    className="p-1 text-muted-foreground shrink-0"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleShortcutDelete(shortcut.id)}
                    className="p-1 text-muted-foreground shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <div className="p-1 text-muted-foreground/40 cursor-grab shrink-0">
                    <GripVertical className="w-4 h-4" />
                  </div>
                </div>
              ))}

              <button
                onClick={() => {
                  setShortcutForm({ name: '', url: '' })
                  setShortcutModal({ open: true, editing: null })
                }}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-dashed border-border hover:bg-muted/40 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl border-2 border-dashed border-primary/40 flex items-center justify-center shrink-0">
                  <Plus className="w-5 h-5 text-primary" />
                </div>
                <span className="text-sm font-medium text-primary">Add shortcut</span>
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Latest News Carousel */}
      <section className="mb-6">
        <h2 className="text-sm font-semibold text-foreground mb-3 font-heading">Latest News</h2>
        <Carousel opts={{ loop: true }} setApi={setCarouselApi} className="w-full">
          <CarouselContent>
            {newsItems.map((item, i) => (
              <CarouselItem key={i}>
                <a href={item.url} target="_blank" rel="noopener noreferrer">
                  <Card className="overflow-hidden">
                    <div className="relative h-48">
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-muted" />
                      )}
                      <div className="absolute inset-0 bg-linear-to-t from-black/80 via-black/30 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <p className="text-xs text-white/70 mb-1">
                          {item.publishDate
                            ? (() => {
                                const d = new Date(item.publishDate)
                                return isNaN(d.getTime())
                                  ? item.publishDate
                                  : d.toLocaleDateString('en-NZ', {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric',
                                    })
                              })()
                            : ''}
                        </p>
                        <p className="text-sm font-semibold text-white line-clamp-2 leading-snug">
                          {item.title}
                        </p>
                      </div>
                    </div>
                  </Card>
                </a>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        <div className="flex justify-center gap-2 mt-3">
          {newsItems.map((_, i) => (
            <button
              key={i}
              onClick={() => carouselApi?.scrollTo(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === carouselIndex ? 'w-6 bg-primary' : 'w-2 bg-muted-foreground/30'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Daily Survey */}
      <section className="mb-6">
        <div
          className={`w-full rounded-xl shadow-md ${
            hasRespondedToday
              ? 'bg-muted border border-border'
              : 'bg-linear-to-r from-accent to-accent/80 text-accent-foreground'
          }`}
        >
          {/* Header row — tapping opens modal (not responded) or toggles results (responded) */}
          <button
            onClick={() => (hasRespondedToday ? setSurveyExpanded((e) => !e) : setShowSurvey(true))}
            className="w-full flex items-center justify-between p-4 text-left active:scale-[0.98] transition-transform"
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  hasRespondedToday ? 'bg-accent/20' : 'bg-primary-foreground/20'
                }`}
              >
                {hasRespondedToday ? (
                  <CheckCircle2 className="w-5 h-5 text-accent" />
                ) : (
                  <BarChart3 className="w-5 h-5" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className={`font-semibold ${hasRespondedToday ? 'text-foreground' : ''}`}>
                    Daily Survey
                  </p>
                  {currentStreak > 0 && (
                    <span className="flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-orange-100 text-orange-600">
                      <Flame className="w-3 h-3" />
                      {currentStreak}
                    </span>
                  )}
                </div>
                <p
                  className={`text-xs ${hasRespondedToday ? 'text-muted-foreground' : 'opacity-90'}`}
                >
                  {hasRespondedToday
                    ? `Completed today ✓ · ${surveyExpanded ? 'Hide' : 'Show'} results`
                    : 'Share your view'}
                </p>
              </div>
            </div>
            <ChevronRight
              className={`w-5 h-5 shrink-0 transition-transform duration-200 ${
                hasRespondedToday ? 'text-muted-foreground' : ''
              } ${hasRespondedToday && surveyExpanded ? 'rotate-90' : ''}`}
            />
          </button>

          {/* Inline results — shown only when expanded after responding */}
          {hasRespondedToday && surveyExpanded && surveyData && surveyData.results.length > 0 && (
            <div className="px-4 pb-4 space-y-3 border-t border-border/50 pt-3">
              <p className="text-sm text-muted-foreground">{surveyData.question}</p>
              {(() => {
                const maxPct = Math.max(...surveyData.results.map((r) => r.percentage), 1)
                return surveyData.results.map((result) => (
                  <div key={result.optionValue} className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground truncate mr-2">{result.optionLabel}</span>
                      <span className="font-medium text-foreground shrink-0">
                        {result.percentage}%
                      </span>
                    </div>
                    <div className="h-3 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary/40 rounded-full transition-all duration-500"
                        style={{ width: `${(result.percentage / maxPct) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">{result.votes} responses</p>
                  </div>
                ))
              })()}
              <button
                onClick={() => setShowSurvey(true)}
                className="text-xs text-primary font-medium pt-1"
              >
                {surveyData.totalVotes} {surveyData.totalVotes === 1 ? 'member' : 'members'}{' '}
                responded · Tap to view details
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Past surveys link */}
      <div className="flex justify-end -mt-4 mb-6">
        <button
          onClick={openHistory}
          className="flex items-center gap-1.5 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 px-3 py-1.5 rounded-full transition-colors"
        >
          <History className="w-3.5 h-3.5" />
          Past surveys
        </button>
      </div>

      <PoweredByFooter />

      {/* Daily Survey Modal */}
      <DailySurvey
        isOpen={showSurvey}
        onClose={() => setShowSurvey(false)}
        surveyData={surveyData}
        loading={surveyLoading}
        onSubmit={submitResponse}
      />

      {/* Survey History Modal */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col gap-0 p-0">
          <DialogHeader className="px-5 pt-5 pb-4 border-b border-border shrink-0">
            <DialogTitle className="flex items-center gap-2 text-base">
              <History className="w-4 h-4 text-primary" />
              Past surveys
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 p-5 space-y-2">
            {historyLoading && (
              <div className="space-y-4 animate-pulse">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-24 bg-muted rounded-xl" />
                ))}
              </div>
            )}
            {!historyLoading && historyItems.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <History className="w-10 h-10 text-muted-foreground/30 mb-3" />
                <p className="text-sm font-medium text-muted-foreground">
                  Past surveys will appear here soon
                </p>
              </div>
            )}
            {!historyLoading &&
              historyItems.map((item) => {
                const isExpanded = expandedHistory.has(item.questionId)
                const maxPct = Math.max(...item.results.map((r) => r.percentage), 1)
                const dateLabel = item.activeDate
                  ? new Date(item.activeDate).toLocaleDateString('en-NZ', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
                  : null
                return (
                  <div
                    key={item.questionId}
                    className="border border-border rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() =>
                        setExpandedHistory((prev) => {
                          const next = new Set(prev)
                          if (next.has(item.questionId)) next.delete(item.questionId)
                          else next.add(item.questionId)
                          return next
                        })
                      }
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground line-clamp-2 leading-snug">
                          {item.question}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {dateLabel && (
                            <span className="text-xs text-muted-foreground">{dateLabel}</span>
                          )}
                          {item.userAnswerLabel ? (
                            <span className="flex items-center gap-1 text-xs text-accent font-medium">
                              <CheckCircle2 className="w-3 h-3" />
                              Answered
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground italic">
                              Not answered
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight
                        className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                      />
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
                        <div className="space-y-3">
                          {item.results.map((result) => {
                            const isSelected = result.optionValue === item.userAnswer
                            return (
                              <div key={result.optionValue} className="space-y-1.5">
                                <div className="flex justify-between text-sm">
                                  <span
                                    className={
                                      isSelected ? 'font-medium text-primary' : 'text-foreground'
                                    }
                                  >
                                    {result.optionLabel}
                                    {isSelected && ' ✓'}
                                  </span>
                                  <span className="font-medium text-foreground">
                                    {result.percentage}%
                                  </span>
                                </div>
                                <div className="h-3 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all duration-500 ${isSelected ? 'bg-primary' : 'bg-primary/40'}`}
                                    style={{ width: `${(result.percentage / maxPct) * 100}%` }}
                                  />
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {result.votes} responses
                                </p>
                              </div>
                            )
                          })}
                        </div>
                        <div className="rounded-lg bg-muted/50 px-3 py-2">
                          <p className="text-xs text-muted-foreground">
                            Total:{' '}
                            <span className="font-medium text-foreground">{item.totalVotes}</span>{' '}
                            {item.totalVotes === 1 ? 'member' : 'members'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add / Edit Shortcut Modal */}
      <Dialog
        open={shortcutModal.open}
        onOpenChange={(open) => {
          if (!open) setShortcutModal({ open: false, editing: null })
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{shortcutModal.editing ? 'Edit shortcut' : 'Add shortcut'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Name</label>
              <input
                type="text"
                value={shortcutForm.name}
                onChange={(e) => setShortcutForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Your Company Website"
                className="w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-background focus:outline-hidden focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">URL</label>
              <input
                type="url"
                value={shortcutForm.url}
                onChange={(e) => setShortcutForm((f) => ({ ...f, url: e.target.value }))}
                placeholder="e.g. reinz.co.nz"
                className="w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-background focus:outline-hidden focus:ring-2 focus:ring-primary"
              />
            </div>
            <button
              onClick={handleShortcutSave}
              disabled={!shortcutForm.name.trim() || !shortcutForm.url.trim()}
              className="w-full bg-primary text-primary-foreground rounded-lg py-2.5 text-sm font-medium disabled:opacity-50 mt-1"
            >
              {shortcutModal.editing ? 'Save changes' : 'Add shortcut'}
            </button>
            {shortcutModal.editing && (
              <button
                onClick={() => {
                  if (!window.confirm('Remove this shortcut?')) return
                  saveShortcuts(shortcuts.filter((s) => s.id !== shortcutModal.editing!.id))
                  setShortcutModal({ open: false, editing: null })
                }}
                className="w-full text-destructive text-sm font-medium py-2"
              >
                Remove shortcut
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
